/**
 * Icon Component
 * 
 * Vector icon component with automatic dark/light theme support
 */

import { IconName, getIconSVG } from './icons';

export interface IconProps {
  name: IconName;
  size?: number | string;
  className?: string;
  color?: string;
  withBackground?: boolean;
  backgroundClassName?: string;
}

import { useThemeStore } from '../../store/theme.store';

// Get theme from store or fallback to system preference
const getTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  
  // Try to get from Zustand store (if available)
  try {
    const mode = useThemeStore.getState().mode;
    if (mode === 'dark' || mode === 'light') {
      return mode;
    }
  } catch {
    // Store not available, fallback to system
  }
  
  // Check for data-mode attribute
  const mode = document.documentElement.getAttribute('data-mode');
  if (mode === 'dark' || mode === 'light') {
    return mode as 'light' | 'dark';
  }
  
  // Fallback to system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  
  return 'light';
};

export function Icon({
  name,
  size = 24,
  className = '',
  color,
  withBackground = false,
  backgroundClassName = '',
}: IconProps) {
  const theme = getTheme();
  const iconSVG = getIconSVG(name, theme);

  const sizeValue = typeof size === 'number' ? `${size}px` : size;
  const iconColor = color || 'currentColor';

  const iconElement = (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ color: iconColor }}
    >
      {iconSVG}
    </svg>
  );

  if (withBackground) {
    const bgClass = backgroundClassName || 
      `bg-surface-tertiary dark:bg-surface-secondary rounded-full p-2 border border-border`;
    
    return (
      <div className={bgClass}>
        {iconElement}
      </div>
    );
  }

  return iconElement;
}

